import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NavVertical from './NavVertical';
import FloatingEyes from './FloatingEyes';
import DecorativeQuimbombo from './DecorativeQuimbombo';
import DecorativeCauri from './DecorativeCauri';
import AnyePote2 from './icons/AnyePote2';
import AnyeMosca2 from './icons/AnyeMosca2';
import AnyeMosquito2 from './icons/AnyeMosquito2';
import AnyeVistasBarco1 from './icons/AnyeVistasBarco1';
import AnyeVistasBarco3 from './icons/AnyeVistasBarco3';
import AnyeVistasBarco4 from './icons/AnyeVistasBarco4';
import VisitorRegistrationIcon from './icons/VisitorRegistrationIcon';
import AnyeCandado from './icons/AnyeCandado';
import BarcoVolteado from './icons/BarcoVolteado';
import AnyeGrillete from './icons/AnyeGrillete';
import AnyeEsposas from './icons/AnyeEsposas';

const CREDITOS_IMAGES = [
  process.env.PUBLIC_URL + '/images/creditos/logo2.png',
  process.env.PUBLIC_URL + '/images/creditos/logo3.png',
];

const sidebarIcons = [
  { icon: AnyePote2, to: '/Gispert2', label: 'nav.gispert2', top: '8%', left: '8%' },
  { icon: AnyeMosca2, to: '/Gispert6', label: 'nav.gispert6', top: '48%', right: '10%' },
  { icon: AnyeMosquito2, to: '/Gispert5', label: 'nav.gispert5', top: '66%', right: '18%' },
  { icon: AnyeEsposas, to: '/Gispert1', label: 'nav.gispert1', top: '82%', left: '12%' },
  { icon: AnyeVistasBarco4, to: '/Gispert4', label: 'nav.gispert4', top: '92%', right: '12%' },
  { icon: AnyeGrillete, to: '/Gispert3', label: 'nav.gispert3', top: '52%', left: '6%' },
  { icon: AnyeVistasBarco1, to: '/Museo1', label: 'nav.museo1', top: '34%', left: '10%' },
  { icon: AnyeVistasBarco3, to: '/Museo2', label: 'nav.museo2', top: '14%', right: '12%' },
  { icon: BarcoVolteado, to: '/Introduccion', label: 'nav.introduccion', top: '74%', left: '14%' },
  { icon: AnyeCandado, to: '/Instructivo', label: 'nav.instructivo', top: '60%', left: '4%' },
  { icon: VisitorRegistrationIcon, to: '/VisitorRegistration', label: 'nav.newsletter', top: '82%', right: '12%' },
];

const Home = () => {
  const { t } = useTranslation();
  const [creditsExpanded, setCreditsExpanded] = useState(false);
  const creditsRef = useRef(null);

  useEffect(() => {
    if (!creditsExpanded) return;

    const handleOutsideClick = (event) => {
      if (creditsRef.current && !creditsRef.current.contains(event.target)) {
        setCreditsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [creditsExpanded]);

  return (
    <section className="HomeHero">
      <div className="HomeHero-image">
        <div className="HomeHero-composition">
          <img
            className="HomeHero-plants"
            src={process.env.PUBLIC_URL + '/images/Plantas_solas.svg'}
            alt="Museografías Marítimas y Esclavitud"
          />
          <img
            className="HomeHero-boat"
            src={process.env.PUBLIC_URL + '/images/Barco_solo_sin_fondo.svg'}
            alt="Barco"
          />
        </div>
      </div>

      <DecorativeQuimbombo />
      <DecorativeCauri />
      <FloatingEyes />

      <nav className="HomeHero-nav" aria-label="Secciones">
        <NavVertical />

        {sidebarIcons.map(({ icon: Icon, to, label, top, left, right }, i) => {
          const tooltipText = label ? t(label) : null;
          const baseClass = 'HomeHero-navIcon';
          const modClass = 'HomeHero-navIcon--link';
          const style = { top, ...(left != null ? { left } : {}), ...(right != null ? { right } : {}) };

          return (
            <Link
              key={i}
              to={to}
              className={`${baseClass} ${modClass}`}
              style={style}
              data-tooltip={tooltipText}
            >
              <Icon />
            </Link>
          );
        })}
      </nav>

      <footer
        ref={creditsRef}
        className={`HomeLicense-bar${creditsExpanded ? ' HomeLicense-bar--expanded' : ''}`}
        id="credits"
        aria-label="Créditos y licencia"
      >
        <div className="HomeLicense-content">
          <div className="HomeLicense-row HomeLicense-license">
            <button
              type="button"
              className="HomeLicense-titleBtn"
              onClick={() => setCreditsExpanded((e) => !e)}
              aria-expanded={creditsExpanded}
              aria-label={creditsExpanded ? t('creditos.closeCredits') : t('creditos.showCredits')}
            >
              Desarmar un aparato técnico colonial
              <span className="HomeLicense-chevron" aria-hidden="true" />
            </button>
            {' © 2026 '}
            <span>Anyely Marín Cisneros</span>
            {' · '}
            <a
              className="HomeLicense-link"
              href="https://creativecommons.org/licenses/by-nc-nd/4.0/"
              target="_blank"
              rel="noopener noreferrer"
            >
              CC BY-NC-ND 4.0
              <img src="https://mirrors.creativecommons.org/presskit/icons/cc.svg" alt="CC" />
              <img src="https://mirrors.creativecommons.org/presskit/icons/by.svg" alt="BY" />
              <img src="https://mirrors.creativecommons.org/presskit/icons/nc.svg" alt="NC" />
              <img src="https://mirrors.creativecommons.org/presskit/icons/nd.svg" alt="ND" />
            </a>
            {' · '}
            <span className="HomeLicense-role">
              <span className="HomeLicense-label">{t('creditos.investigadoraLabel')}</span>
              <a href="mailto:anyely.marin.c@gmail.com">Anyely Marín Cisneros</a>
            </span>
            {' · '}
            <a href="mailto:anyely.marin.c@gmail.com">anyely.marin.c@gmail.com</a>
          </div>

          <div className="HomeLicense-row HomeLicense-fundingRow" aria-label={t('creditos.fundingNoticeLabel')}>
            <span className="HomeLicense-fundingLogos" aria-label={t('creditos.fundingLogosLabel')}>
              {CREDITOS_IMAGES.map((src, i) => (
                <span key={i} className="HomeLicense-imageInline">
                  <img
                    src={src}
                    alt={t('creditos.imageAlt', { number: i + 1 })}
                  />
                </span>
              ))}
            </span>
            <span className="HomeLicense-euDisclaimer">{t('creditos.euDisclaimer')}</span>
          </div>
        </div>
        <div className="HomeLicense-popover" aria-hidden={!creditsExpanded}>
          <p className="HomeLicense-intro" dangerouslySetInnerHTML={{ __html: t('creditos.intro') }} />
          <p className="HomeLicense-residency" dangerouslySetInnerHTML={{ __html: t('creditos.residency') }} />
          <div className="HomeLicense-researcher">
            <p className="HomeLicense-researcherHead">
              <strong>{t('creditos.investigadoraLabel')}</strong>{' '}
              <span>Anyely Marín Cisneros</span>
            </p>
            <p className="HomeLicense-bio" dangerouslySetInnerHTML={{ __html: t('creditos.bio1') }} />
            <p className="HomeLicense-bio" dangerouslySetInnerHTML={{ __html: t('creditos.bio2') }} />
            <p className="HomeLicense-researcherHead">
              <strong>{t('creditos.ilustracionLabel')}</strong>{' '}
              <a href="https://www.instagram.com/laflordeltamarindo/" target="_blank" rel="noopener noreferrer">Elizabeth Montero</a>
            </p>
            <p className="HomeLicense-researcherHead">
              <strong>{t('creditos.disenoLabel')}</strong>{' '}
              <a href="https://www.linkedin.com/in/amarantavc/" target="_blank" rel="noopener noreferrer">Amaranta Villegas</a>
              {' - '}
              <a href="https://www.linkedin.com/in/mvgregoryj/" target="_blank" rel="noopener noreferrer">Gregory Muñoz</a>
            </p>
          </div>
        </div>
      </footer>
    </section>
  );
};

export default Home;
